// Enhanced ActivityCard.jsx - Modern, professional design with rounded corners
import React from 'react';

const ActivityCard = ({ activity, onClick }) => {
  const {
    title,
    class_name,
    due_date,
    completion_status
  } = activity;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status configuration
  const getStatusConfig = () => {
    switch (completion_status) {
      case 'completed':
        return {
          text: '✓ Completed',
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-200'
        };
      case 'in_progress':
        return {
          text: '⏳ In Progress',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200'
        };
      case 'not_started':
      default:
        return {
          text: '○ Not Started',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200'
        };
    }
  };

  // Check if overdue
  const isOverdue = () => {
    if (!due_date) return false;
    return new Date(due_date) < new Date() && completion_status !== 'completed';
  };

  const statusConfig = getStatusConfig();

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl border border-gray-100 flex flex-col cursor-pointer"
    >
      {/* Header with Gradient - IMPORTANT: Keep rounded-t-3xl */}
      <div className="relative h-48 overflow-hidden rounded-t-3xl">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-red-400 to-pink-400 opacity-90 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Pattern Overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />

        {/* Status Badge */}
        <span className={`absolute top-4 left-4 ${statusConfig.bgColor} ${statusConfig.textColor} text-xs font-semibold px-4 py-2 rounded-full shadow-lg border ${statusConfig.borderColor} backdrop-blur-sm`}>
          {statusConfig.text}
        </span>

        {/* Icon Badge */}
        <div className="absolute top-4 right-4">
          <div className="w-14 h-14 bg-white bg-opacity-20 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-white border-opacity-30">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>

        {/* Title Overlay at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
          <h3 className="text-white text-xl font-bold leading-tight drop-shadow-lg">
            {title}
          </h3>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6 flex-1 flex flex-col">
        {/* Info Pills */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <span className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 text-[#1D3469] font-semibold rounded-full text-xs border border-blue-200">
            📚 {class_name || 'Class'}
          </span>
          <span className={`px-3 py-1.5 ${isOverdue() ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-700 border-red-200' : 'bg-gradient-to-r from-green-100 to-green-200 text-green-700 border-green-200'} font-semibold rounded-full text-xs border`}>
            📅 {formatDate(due_date)}
          </span>
        </div>

        {/* Overdue Warning */}
        {isOverdue() && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-red-700 font-medium">This activity is overdue!</span>
          </div>
        )}

        {/* Progress Section */}
        {completion_status === 'in_progress' && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-600">Progress</span>
              <span className="text-xs font-bold text-blue-600">In Progress</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full w-1/2 transition-all duration-300"></div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="mt-auto w-full bg-gradient-to-r from-[#D67C2C] to-[#e06b35] hover:from-[#e06b35] hover:to-[#F17C45] text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-200 text-sm flex items-center justify-center gap-2"
        >
          {completion_status === 'not_started' ? (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Start Activity
            </>
          ) : completion_status === 'completed' ? (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Activity
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Continue
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ActivityCard;
